const post = {
  slug: 'faceless-youtube-niches',
  cluster: 'niches',
  pillar: true,
  eyebrow: 'Niches',
  title: 'Choosing a niche for a faceless channel',
  description:
    'How to pick something to be about that has demand, that you can still stand in six months, and that you can actually get pictures for.',
  keywords: [
    'faceless youtube niches', 'youtube niche ideas', 'best niche for youtube',
    'choosing a youtube niche', 'faceless channel niche', 'youtube niche selection',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/faceless-youtube-niches.webp',
    alt: 'A set of overlapping circles labelled demand, sustainability and visual material, with a small region where all three meet.',
  },
  lede:
    'Most niche advice optimises for one variable — usually what pays best — and produces channels that die at video nine. There are three constraints, and the niche you want is where they overlap.',
  sections: [
    {
      heading: 'The three constraints',
      body: [
        {
          type: 'para',
          text: 'A niche has to satisfy all three of these. Missing any one of them fails the channel, and each fails it in a characteristic way.',
        },
        {
          type: 'list',
          ordered: true,
          items: [
            '**Demand** — people are looking for this, or would watch it if shown. Fail here and good videos get no impressions at all.',
            '**Sustainability** — you can produce fifty of these without hating it. Fail here and the channel stops at nine.',
            '**Visual material** — the topic can be shown, not just narrated. Fail here and every video looks like every other video, whatever the script says.',
          ],
        },
        {
          type: 'para',
          text: 'The third one is specific to faceless channels and is the one most often skipped. A presenter can carry an abstract topic by being interesting to look at. You cannot.',
        },
      ],
    },
    {
      heading: 'Demand, without the keyword-tool theatre',
      body: [
        {
          type: 'para',
          text: 'You do not need a subscription tool to tell whether a niche has demand. You need to check that videos on the topic exist and get watched by channels that are not already enormous.',
        },
        {
          type: 'para',
          text: 'Search the topic, filter to the last year, and look at what comes back. Three patterns and what each means:',
        },
        {
          type: 'list',
          items: [
            '**Nothing, or only very old videos.** Usually not an opportunity. It is more often a subject nobody searches for.',
            '**Everything from channels with millions of subscribers.** Demand is real, competition is brutal. Viable only if you go much narrower.',
            '**A spread, including channels with a few thousand subscribers getting solid view counts.** This is the good case. It means the topic gets recommended on merit rather than on channel size.',
          ],
        },
        {
          type: 'para',
          text: '[Evaluating a niche](/blogs/how-to-evaluate-a-youtube-niche) covers the full check.',
        },
      ],
    },
    {
      heading: 'Sustainability is the one people lie to themselves about',
      body: [
        {
          type: 'para',
          text: 'The test is not "am I interested in this". It is: **can I list twenty specific videos right now, without research, that I would want to make?**',
        },
        {
          type: 'para',
          text: 'Twenty, written down, before you make one. If you get to eleven and start straining, the niche is too narrow or your interest is thinner than it felt. That is a cheap thing to discover now.',
        },
        {
          type: 'para',
          text: 'The reason this matters more for faceless channels: there is no presenter energy to coast on. A video you were bored making reads as boring, because the only thing carrying it is the writing, and bored writing is flat writing.',
        },
        {
          type: 'callout',
          title: 'The money-first trap',
          text: 'Picking finance or tech because the ad rates are higher, with no interest in either, is the most reliable way to produce twenty interchangeable videos and stop. High RPM on zero views is zero. See [what RPM actually means](/blogs/youtube-rpm-by-niche).',
        },
      ],
    },
    {
      heading: 'Visual material is a hard constraint',
      body: [
        {
          type: 'para',
          text: 'Before committing, ask: for a typical video in this niche, what is actually on screen?',
        },
        {
          type: 'para',
          text: 'Good answers are concrete: real photographs of the people and places involved, diagrams of a mechanism, maps, charts of real figures, archive footage, screen recordings. Bad answers are abstract: generic stock footage of someone typing, an AI image of a concept, a slow pan over a gradient.',
        },
        {
          type: 'para',
          text: 'Niches heavy on real named entities — companies, disasters, people, places, products, historical events — are the strongest here, because named things have images of them. Niches about feelings, advice, or abstractions are the weakest.',
        },
      ],
    },
    {
      heading: 'Narrow beats broad, consistently',
      body: [
        {
          type: 'para',
          text: '"History" is not a niche. "The collapse of specific companies" is. The narrow version is easier to rank in, easier to be recognisable for, and easier to write because the research compounds — the fifth video in a narrow niche is faster to make than the first, which is never true in a broad one.',
        },
        {
          type: 'para',
          text: 'The fear is running out of topics. In practice, channels almost never die from exhausting a narrow niche; they die from being indistinguishable in a broad one. [Why narrow wins](/blogs/narrow-youtube-niche) goes into it.',
        },
      ],
    },
    {
      heading: 'A workable shortlist method',
      body: [
        {
          type: 'list',
          ordered: true,
          items: [
            'Write down five subjects you would read about unpaid.',
            'For each, narrow it until it names a specific *kind* of video, not a field.',
            'For each narrowed version, list twenty topics. Cross off any where you stall before twelve.',
            'For the survivors, check the search results for the demand pattern above.',
            'For the survivors of that, answer "what is on screen" concretely. Cross off any where the honest answer is generic stock.',
            'Pick whichever is left that you would most want to spend a year on.',
          ],
        },
        {
          type: 'para',
          text: 'If nothing survives, the usual culprit is step two — the narrowing was not specific enough to produce twenty distinct videos, or was so specific it produced eight.',
        },
      ],
    },
  ],
  faq: [
    {
      q: 'Can I change niche later?',
      a: 'Yes, and plenty of channels have. The cost is that subscribers gained on the old topic largely will not watch the new one, so early performance on the new material looks worse than it is. Changing at twenty videos is cheap; at two hundred it is closer to starting again.',
    },
    {
      q: 'Should I pick a niche based on what pays best?',
      a: 'Ad rates vary by a lot between subjects, so it is a real input — but it is the last filter, not the first. A niche you cannot sustain earns nothing regardless of its rate.',
    },
    {
      q: 'Is it a problem if a niche already has big channels in it?',
      a: 'Not necessarily, and their existence is evidence of demand. What matters is whether smaller channels also get views. If every video on the first page is from a million-subscriber channel, the topic is being served by brand rather than by merit.',
    },
  ],
  related: ['how-to-evaluate-a-youtube-niche', 'narrow-youtube-niche', 'youtube-niches-to-avoid'],
};

export default post;
